import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import MobileTopBarClean from '../components/MobileTopBarClean';
import { usePortfolioStats } from '../hooks/usePortfolioStats';
import { useIsMobile } from '../hooks/useIsMobile';

const SettingsPage = () => {
  const { t } = useTranslation();
  const { language, changeLanguage, loading: languageLoading } = useLanguage();
  const { getHeaderStats } = usePortfolioStats();
  const isMobile = useIsMobile();
  const [saveMessage, setSaveMessage] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(language);


  // Update selected language when context changes
  React.useEffect(() => {
    setSelectedLanguage(language);
  }, [language]);

  const handleLanguageChange = async () => {
    if (selectedLanguage === language) {
      setSaveMessage({ type: 'info', text: t('settings:messages.languageChanged') });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    try {
      await changeLanguage(selectedLanguage);
      setSaveMessage({ type: 'success', text: t('settings:messages.languageChanged') });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({ type: 'error', text: t('settings:messages.saveError') });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <MobileTopBarClean stats={getHeaderStats()} />

      {/* Main Content */}
      <main
        className="px-4 pb-8 pt-16 sm:px-6 lg:px-8 max-w-4xl mx-auto"
        style={{ marginTop: isMobile ? '80px' : '90px' }}
      >
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
          {/* Page Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            {t('settings:pageTitle')}
          </h1>

          {/* Language Section */}
          <section className="mb-8 settings-section">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {t('settings:sections.language.title')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('settings:sections.language.description')}
            </p>

            {/* Current Language Display */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 settings-label">
                {t('settings:sections.language.labels.currentLanguage')}
              </label>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-lg font-medium">
                  {language === 'en' ? '🇺🇸 English' : '🇮🇱 עברית'}
                </span>
              </div>
            </div>

            {/* Language Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-4 settings-label">
                {t('settings:sections.language.labels.selectLanguage')}
              </label>

              <div className="space-y-3">
                {/* English Option */}
                <button
                  onClick={() => setSelectedLanguage('en')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all duration-200 language-option ${
                    selectedLanguage === 'en'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🇺🇸</span>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">English</div>
                      <div className="text-sm text-gray-600">United States</div>
                    </div>
                  </div>
                  {selectedLanguage === 'en' && (
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </button>

                {/* Hebrew Option */}
                <button
                  onClick={() => setSelectedLanguage('he')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all duration-200 language-option ${
                    selectedLanguage === 'he'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🇮🇱</span>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">עברית</div>
                      <div className="text-sm text-gray-600">ישראל</div>
                    </div>
                  </div>
                  {selectedLanguage === 'he' && (
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </button>
              </div>
            </div>

            {/* Save Message */}
            {saveMessage && (
              <div
                className={`mb-6 p-4 rounded-lg ${
                  saveMessage.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : saveMessage.type === 'error'
                    ? 'bg-red-50 border border-red-200 text-red-800'
                    : 'bg-blue-50 border border-blue-200 text-blue-800'
                }`}
              >
                <div className="flex items-center gap-2">
                  {saveMessage.type === 'success' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {saveMessage.type === 'error' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  <span className="font-medium">{saveMessage.text}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={handleLanguageChange}
                disabled={languageLoading || selectedLanguage === language}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                  selectedLanguage === language
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 active:transform active:scale-95'
                }`}
              >
                {languageLoading ? t('common:states.processing') : t('settings:actions.save')}
              </button>
            </div>
          </section>

          {/* Divider */}
          <hr className="my-8 border-gray-200" />

          {/* Info Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <svg
                className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">
                  {language === 'en' ? 'Language Settings' : 'הגדרות שפה'}
                </h3>
                <p className="text-sm text-blue-800">
                  {language === 'en'
                    ? 'Your language preference will be saved and applied across all pages. The AI assistant (Gideon) will automatically respond in your selected language.'
                    : 'העדפת השפה שלך תישמר ותחול על כל הדפים. עוזר הבינה המלאכותית (גדעון) יגיב אוטומטית בשפה שבחרת.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
